/**
 * Lazy product-video carousel.
 * Posters can be swiped immediately; provider players are created only after Play is clicked.
 */
(() => {
  if (customElements.get('product-videos')) return;

  class ProductVideos extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.product-videos__list');
      this.previousButton = this.querySelector('[data-product-videos-direction="-1"]');
      this.nextButton = this.querySelector('[data-product-videos-direction="1"]');
      this.modal = this.querySelector('[data-product-video-modal]');
      this.modalPlayer = this.querySelector('[data-product-video-modal-player]');
      this.modalCloseButton = this.querySelector('.product-videos__modal-close');
      this.addEventListener('click', this.onClick);
      this.list?.addEventListener('scroll', this.onScroll, { passive: true });
      this.prepareYouTubePosters();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this);
      }
      requestAnimationFrame(() => this.updateControls());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.list?.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('product-videos-modal-open');
      this.resizeObserver?.disconnect();
    }

    onScroll = () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateControls());
    };

    onClick = (event) => {
      if (event.target.closest('[data-product-video-close]')) {
        this.closeModal();
        return;
      }

      const arrow = event.target.closest('[data-product-videos-direction]');
      if (arrow) {
        this.scrollBySlide(Number(arrow.dataset.productVideosDirection));
        return;
      }

      const card = event.target.closest('[data-product-video]');
      if (!card || card.classList.contains('is-playing')) return;

      const player = this.createPlayer(card.dataset.videoUrl);
      if (!player) {
        const error = this.querySelector('.product-videos__error');
        if (error) {
          error.textContent = 'This video link could not be played. Please check the URL in the theme editor.';
          error.hidden = false;
        }
        return;
      }

      this.openModal(player, card);
    };

    onKeydown = (event) => {
      if (event.key === 'Escape' && this.modalOpen) this.closeModal();
    };

    openModal(player, card) {
      if (!this.modal || !this.modalPlayer) return;
      this.activeCard = card;
      this.modalOpen = true;
      card.classList.add('is-playing');
      this.modalPlayer.replaceChildren(player);
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-videos-modal-open');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        this.modalCloseButton?.focus();
      });
    }

    closeModal() {
      if (!this.modalOpen) return;
      this.modalOpen = false;
      this.modal?.classList.remove('is-open');
      this.modal?.setAttribute('aria-hidden', 'true');
      if (this.modal) this.modal.hidden = true;
      this.modalPlayer?.replaceChildren();
      document.documentElement.classList.remove('product-videos-modal-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.activeCard?.classList.remove('is-playing');
      this.activeCard?.focus();
      this.activeCard = null;
    }

    scrollBySlide(direction) {
      const slide = this.list?.querySelector('.product-videos__slide');
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      this.list.scrollBy({ left: direction * (slide.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    updateControls() {
      if (!this.list) return;
      const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
      const isScrollable = maxScroll > 2;
      this.classList.toggle('is-scrollable', isScrollable);
      if (this.previousButton) this.previousButton.disabled = !isScrollable || this.list.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = !isScrollable || this.list.scrollLeft >= maxScroll - 2;
    }

    prepareYouTubePosters() {
      this.querySelectorAll('[data-product-video]').forEach((card) => {
        const poster = card.querySelector('.product-videos__poster--generated');
        if (!poster) return;
        const parsed = this.parseUrl(card.dataset.videoUrl);
        if (parsed?.provider === 'youtube') {
          // Keep the neutral fallback visible if the thumbnail request fails.
          poster.addEventListener('load', () => {
            card.querySelector('.product-videos__placeholder')?.remove();
          }, { once: true });

          poster.src = `https://i.ytimg.com/vi/${parsed.id}/hqdefault.jpg`;
          poster.removeAttribute('width');
          poster.removeAttribute('height');
        }
      });
    }

    parseUrl(rawUrl) {
      try {
        const url = new URL(rawUrl, window.location.origin);
        const host = url.hostname.replace(/^www\./, '');
        const parts = url.pathname.split('/').filter(Boolean);

        if (host === 'youtu.be') return { provider: 'youtube', id: parts[0] };
        if (host.endsWith('youtube.com')) {
          const id = url.searchParams.get('v') || (['shorts', 'embed', 'live'].includes(parts[0]) ? parts[1] : '');
          return { provider: 'youtube', id };
        }
        if (host.endsWith('instagram.com')) {
          const typeIndex = parts.findIndex((part) => ['p', 'reel', 'tv'].includes(part));
          if (typeIndex >= 0 && parts[typeIndex + 1]) return { provider: 'instagram', type: parts[typeIndex], id: parts[typeIndex + 1] };
        }
        if (host.endsWith('vimeo.com')) {
          return { provider: 'vimeo', id: [...parts].reverse().find((part) => /^\d+$/.test(part)) };
        }
        if (host.endsWith('tiktok.com')) {
          const videoIndex = parts.indexOf('video');
          if (videoIndex >= 0 && parts[videoIndex + 1]) return { provider: 'tiktok', id: parts[videoIndex + 1] };
        }
        if (/\.(mp4|webm|ogg)(?:$|[?#])/i.test(url.href)) return { provider: 'direct', src: url.href };
        return { provider: 'generic', src: url.href };
      } catch (error) {
        return null;
      }
    }

    createPlayer(rawUrl) {
      const parsed = this.parseUrl(rawUrl);
      if (!parsed?.id && !parsed?.src) return null;

      if (parsed.provider === 'direct') {
        const video = document.createElement('video');
        video.src = parsed.src;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'metadata';
        return video;
      }

      const iframe = document.createElement('iframe');
      iframe.title = 'Product video';
      iframe.loading = 'eager';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      // Prevent embedded pages from navigating the storefront or opening external tabs.
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');

      switch (parsed.provider) {
        case 'youtube':
          iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(parsed.id)}?autoplay=1&rel=0`;
          break;
        case 'instagram':
          iframe.src = `https://www.instagram.com/${parsed.type}/${encodeURIComponent(parsed.id)}/embed/captioned/`;
          break;
        case 'vimeo':
          iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(parsed.id)}?autoplay=1`;
          break;
        case 'tiktok':
          iframe.src = `https://www.tiktok.com/player/v1/${encodeURIComponent(parsed.id)}?autoplay=1&controls=1`;
          break;
        default:
          iframe.src = parsed.src;
      }
      return iframe;
    }
  }

  customElements.define('product-videos', ProductVideos);
})();
